import { useEffect, useState } from "react";
import { api, formatApiError, formatMoney, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, CreditCard, Car, GraduationCap, Home as HomeIcon, PiggyBank, Landmark } from "lucide-react";

// Category → label + icon + palette
const CATEGORIES = {
  credit_card:   { label: "Credit card",   icon: CreditCard,     accent: "text-[#8C3A30]", bg: "bg-[#FDF2F0]", border: "border-[#F0D5CE]" },
  car_loan:      { label: "Car loan",      icon: Car,            accent: "text-[#8C5D30]", bg: "bg-[#FFF6ED]", border: "border-[#F3E1CF]" },
  student_loan:  { label: "Student loan",  icon: GraduationCap,  accent: "text-[#59605C]", bg: "bg-[#F2EFEB]", border: "border-[#E3DED6]" },
  mortgage:      { label: "Mortgage",      icon: HomeIcon,       accent: "text-[#3B5B3E]", bg: "bg-[#EEF3EC]", border: "border-[#D5E1D3]" },
  personal_loan: { label: "Personal loan", icon: PiggyBank,      accent: "text-[#59605C]", bg: "bg-[#F2EFEB]", border: "border-[#E3DED6]" },
  other_loan:    { label: "Other loan",    icon: Landmark,       accent: "text-[#59605C]", bg: "bg-[#F2EFEB]", border: "border-[#E3DED6]" },
};

const CATEGORY_ORDER = ["credit_card", "car_loan", "student_loan", "mortgage", "personal_loan", "other_loan"];

export default function Debts() {
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/debts");
    setDebts(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, upd) => {
    try {
      const { data } = await api.patch(`/debts/${id}`, upd);
      setDebts((prev) => prev.map((d) => (d.id === id ? { ...d, ...data } : d)));
      return data;
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const remove = async (debt) => {
    const backup = { ...debt };
    setDebts((prev) => prev.filter((d) => d.id !== debt.id));
    try {
      await api.delete(`/debts/${debt.id}`);
      toast(`Deleted "${debt.name}"`, {
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data } = await api.post("/debts", {
                name: backup.name, category: backup.category,
                balance: backup.balance, monthly_payment: backup.monthly_payment,
                interest_rate_apr: backup.interest_rate_apr, notes: backup.notes || null,
              });
              setDebts((prev) => [...prev, data]);
              toast.success("Restored");
            } catch (e) { toast.error(formatApiError(e)); }
          },
        },
      });
    } catch (e) {
      setDebts((prev) => [...prev, backup]);
      toast.error(formatApiError(e));
    }
  };

  const groups = CATEGORY_ORDER.map((cat) => ({
    cat,
    meta: CATEGORIES[cat],
    items: debts.filter((d) => d.category === cat),
  })).filter((g) => g.items.length > 0);

  const totalDebt = debts.reduce((s, d) => s + Number(d.balance || 0), 0);
  const monthlyDebtPayments = debts.reduce((s, d) => s + Number(d.monthly_payment || 0), 0);
  const totalByCategory = (cat) =>
    debts.filter((d) => d.category === cat).reduce((s, d) => s + Number(d.balance || 0), 0);

  return (
    <div className="space-y-6" data-testid="debts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Debts & loans</div>
          <h1 className="font-heading text-3xl font-medium text-[#2C302E] mt-1">What you owe</h1>
          <p className="text-[#59605C] mt-1 max-w-2xl">
            Track credit card balances and loan payoffs separately from your cash accounts. <span className="text-[#2C302E] font-medium">Suggested cadence:</span> when you review each bill every month, log in to your provider and update the balance here so you always see the true payoff progress.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowAdd(true)} data-testid="debts-add-button">
          <Plus size={16} /> Add debt
        </button>
      </div>

      {/* Top summary */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="card-soft p-5 bg-[#FDF2F0] border-[#F0D5CE]" data-testid="debts-total-summary">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8C3A30]">Total owed</div>
          <div className="font-heading text-3xl font-semibold text-[#8C3A30] mt-2" data-testid="debts-total-owed">
            {formatMoney(totalDebt)}
          </div>
          <div className="text-xs text-[#59605C] mt-1">Across {debts.length} account{debts.length === 1 ? "" : "s"}</div>
        </div>
        <div className="card-soft p-5" data-testid="debts-monthly-summary">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Monthly debt payments</div>
          <div className="font-heading text-3xl font-semibold text-[#2C302E] mt-2" data-testid="debts-monthly-total">
            {formatMoney(monthlyDebtPayments)}
          </div>
          <div className="text-xs text-[#59605C] mt-1">Sum of every &ldquo;monthly payment&rdquo; field</div>
        </div>
        <div className="card-soft p-5 bg-[#EEF3EC] border-[#D5E1D3]" data-testid="debts-cc-summary">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#3B5B3E]">Credit card balances</div>
          <div className="font-heading text-3xl font-semibold text-[#3B5B3E] mt-2" data-testid="debts-cc-total">
            {formatMoney(totalByCategory("credit_card"))}
          </div>
          <div className="text-xs text-[#59605C] mt-1">
            {debts.filter((d) => d.category === "credit_card").length} card{debts.filter((d) => d.category === "credit_card").length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-[#59605C]">Loading…</div>
      ) : debts.length === 0 ? (
        <div className="card-soft p-8 text-center">
          <div className="text-[#59605C]">No debts tracked yet.</div>
          <button className="btn-secondary mt-3" onClick={() => setShowAdd(true)}>
            <Plus size={14} /> Add your first
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.cat} data-testid={`debts-group-${g.cat}`}>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-heading text-lg font-medium text-[#2C302E]">{g.meta.label}s</h2>
                <div className="text-sm text-[#59605C]">
                  Category total <span className={`font-semibold ${g.meta.accent} ml-1`} data-testid={`debts-cat-total-${g.cat}`}>{formatMoney(totalByCategory(g.cat))}</span>
                </div>
              </div>
              <div className="space-y-2">
                {g.items.map((d) => (
                  <DebtRow key={d.id} debt={d} onPatch={patch} onDelete={() => remove(d)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {showAdd && <AddDebtModal onClose={() => setShowAdd(false)} onSaved={(d) => { setDebts((p) => [...p, d]); setShowAdd(false); }} />}
    </div>
  );
}

// ---------- Inline-editable debt row ----------
function DebtRow({ debt, onPatch, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: debt.name, category: debt.category,
    balance: debt.balance, monthly_payment: debt.monthly_payment,
    interest_rate_apr: debt.interest_rate_apr ?? "",
    notes: debt.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const meta = CATEGORIES[debt.category] || CATEGORIES.other_loan;
  const Icon = meta.icon;

  useEffect(() => {
    setForm({
      name: debt.name, category: debt.category,
      balance: debt.balance, monthly_payment: debt.monthly_payment,
      interest_rate_apr: debt.interest_rate_apr ?? "",
      notes: debt.notes || "",
    });
  }, [debt]);

  const save = async () => {
    setSaving(true);
    try {
      await onPatch(debt.id, {
        name: form.name.trim(),
        category: form.category,
        balance: Number(form.balance || 0),
        monthly_payment: Number(form.monthly_payment || 0),
        interest_rate_apr: form.interest_rate_apr === "" ? null : Number(form.interest_rate_apr),
        notes: form.notes || null,
      });
      toast.success(`Updated ${form.name}`);
      setEditing(false);
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className={`p-4 rounded-2xl border ${meta.border} ${meta.bg}`} data-testid={`debt-edit-${debt.id}`}>
        <div className="grid md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Name</label>
            <input className="input-soft" autoFocus value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              data-testid={`debt-edit-name-${debt.id}`} />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Category</label>
            <select className="input-soft" value={form.category}
              onChange={(e) => setForm({...form, category: e.target.value})}>
              {Object.entries(CATEGORIES).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Balance</label>
            <input type="number" step="0.01" className="input-soft"
              value={form.balance}
              onChange={(e) => setForm({...form, balance: e.target.value})}
              data-testid={`debt-edit-balance-${debt.id}`} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Monthly payment</label>
            <input type="number" step="0.01" className="input-soft"
              value={form.monthly_payment}
              onChange={(e) => setForm({...form, monthly_payment: e.target.value})} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">APR (%)</label>
            <input type="number" step="0.01" className="input-soft"
              placeholder="e.g. 24.99"
              value={form.interest_rate_apr}
              onChange={(e) => setForm({...form, interest_rate_apr: e.target.value})} />
          </div>
          <div className="md:col-span-5">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Notes</label>
            <input className="input-soft" value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})} />
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={save} disabled={saving} data-testid={`debt-edit-save-${debt.id}`}>
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    );
  }

  const apr = debt.interest_rate_apr != null ? `${Number(debt.interest_rate_apr).toFixed(2)}% APR` : "";
  return (
    <div className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-white border border-[#D8CFB9] hover:bg-[#F9F8F6] transition-colors" data-testid={`debt-row-${debt.id}`}>
      <button type="button" onClick={() => setEditing(true)}
        className="flex items-center gap-3 min-w-0 flex-1 text-left"
        title="Click to edit">
        <span className={`p-2 rounded-xl ${meta.bg} ${meta.accent}`}>
          <Icon size={16} />
        </span>
        <div className="min-w-0">
          <div className="text-[#2C302E] font-medium truncate">{debt.name}</div>
          <div className="text-xs text-[#8A918D]">
            {meta.label}
            {apr && <span className="ml-2">· {apr}</span>}
            {debt.last_reviewed_at && <span className="ml-2">· reviewed {formatDate(debt.last_reviewed_at)}</span>}
          </div>
        </div>
      </button>
      <div className="text-right shrink-0">
        <button type="button" onClick={() => setEditing(true)}
          className={`font-heading text-lg font-semibold hover:underline underline-offset-2 decoration-dotted whitespace-nowrap ${meta.accent}`}
          data-testid={`debt-balance-${debt.id}`}>
          {formatMoney(debt.balance)}
        </button>
        <div className="text-[10px] uppercase tracking-widest text-[#8A918D] mt-0.5">
          {Number(debt.monthly_payment) > 0 ? `Monthly ${formatMoney(debt.monthly_payment)}` : "No monthly"}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button className="btn-ghost !px-2 !py-1 text-xs" onClick={() => setEditing(true)} data-testid={`debt-edit-btn-${debt.id}`}>Edit</button>
        <button className="btn-ghost !p-2" onClick={onDelete} aria-label="Delete" data-testid={`debt-del-${debt.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ---------- Add debt modal ----------
function AddDebtModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", category: "credit_card",
    balance: "", monthly_payment: "", interest_rate_apr: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/debts", {
        name: form.name.trim(),
        category: form.category,
        balance: Number(form.balance || 0),
        monthly_payment: Number(form.monthly_payment || 0),
        interest_rate_apr: form.interest_rate_apr === "" ? null : Number(form.interest_rate_apr),
        notes: form.notes || null,
      });
      toast.success(`Added ${data.name}`);
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#2C302E]/50 flex items-center justify-center p-4" onClick={onClose}>
      <form className="card-soft p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="debts-add-form">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-medium">Add debt or loan</h2>
          <button type="button" className="btn-ghost !p-2" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase text-[#59605C]">Name</label>
            <input className="input-soft" placeholder="e.g. Amex Gold, Toyota Camry loan" required autoFocus
              value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
              data-testid="debts-add-name" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Category</label>
            <select className="input-soft" value={form.category}
              onChange={(e) => setForm({...form, category: e.target.value})}
              data-testid="debts-add-category">
              {Object.entries(CATEGORIES).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Current balance</label>
            <input type="number" step="0.01" className="input-soft" placeholder="0.00" required
              value={form.balance} onChange={(e) => setForm({...form, balance: e.target.value})}
              data-testid="debts-add-balance" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Monthly payment</label>
            <input type="number" step="0.01" className="input-soft" placeholder="0.00"
              value={form.monthly_payment}
              onChange={(e) => setForm({...form, monthly_payment: e.target.value})}
              data-testid="debts-add-monthly" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase text-[#59605C]">Interest rate (APR %)</label>
            <input type="number" step="0.01" className="input-soft" placeholder="e.g. 24.99"
              value={form.interest_rate_apr}
              onChange={(e) => setForm({...form, interest_rate_apr: e.target.value})}
              data-testid="debts-add-apr" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving} data-testid="debts-add-submit">
            {saving ? "Adding…" : "Add debt"}
          </button>
        </div>
      </form>
    </div>
  );
}
